import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import './Reporting.css';
import WithLoading from '../WithLoading/WithLoading';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function ReportingView({ t }) {
  const [isHealthy, setIsHealthy] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [graphData, setGraphData] = useState({labels: [], datasets: []});
  const [isLoadingGraph, setIsLoadingGraph] = useState(true);
  const DisplayStatusWithLoading = WithLoading(DisplayStatus);
  const DisplayEnergyGraphWithLoading = WithLoading(DisplayEnergyGraph);

  const params = new URLSearchParams(window.location.search);
  const id = params.get("id");

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_URL + `/qr/info/status/STI%23` + encodeURIComponent(id) + '?time=' + Date.now(), {timeout: 30000});
        setIsHealthy(response.data.healthy == null || response.data.healthy);
      } catch (error) {
        console.error('Error fetching device status:', error);
        alert(t("error_fetching_status"))
      } finally {
        setIsLoadingStatus(false);
      }
    };

    const fetchMonthlyEnergy = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_URL + `/qr/info/energy/STI%23` + encodeURIComponent(id) + '?time=' + Date.now(), {timeout: 30000});
        var labels = [];
        var energyData = [];
        var totalData = [];
        for (let i = response.data.energies.length - 1; i >= 0; i--) {
          console.log(response.data.energies[i]);
          labels.push(t(response.data.energies[i].month));
          const energy = response.data.energies[i].energy;
          const savings = response.data.energies[i].savings;
          if (energy === null){
            energyData.push(null);
            totalData.push(null);
          } else {
            energyData.push(energy);
            totalData.push(energy + savings);
          }
        }

        setGraphData({
          labels: labels,
          datasets: [
            {
              label: t("reporting_view.energy_graph.label"),
              data: energyData,
              borderColor: 'rgba(75, 192, 192, 1)',
              backgroundColor: 'rgba(75, 192, 192, 0.3)',
              fill: true,
              tension: 0.3,
            },
            {
              label: t("reporting_view.savings_graph.label"),
              data: totalData,
              borderColor: 'rgba(255, 206, 86, 1)',
              backgroundColor: 'rgba(255, 206, 86, 0.3)',
              fill: '-1', // Fill to the previous dataset
              tension: 0.3,
              spanGaps: false,
            },
          ],
        });
      } catch (error) {
        console.error('Error fetching energy data:', error);
        alert(t("error_fetching_energy_data"))
      } finally {
        setIsLoadingGraph(false);
      }
    };

    fetchStatus();
    fetchMonthlyEnergy();
  }, [id, t]);

  return (
    <div className="reporting-view-container">
      <h1>{t("reporting_view.title")}</h1>
      <DisplayStatusWithLoading isLoading={isLoadingStatus} t={t} isHealthy={isHealthy} />
      <DisplayEnergyGraphWithLoading isLoading={isLoadingGraph} t={t} graphData={graphData} />
      <div className="content-box">
        <h2>{t("reporting_view.contact_us")}</h2>
        <p>{t("reporting_view.email")}</p>
        <p>{t("reporting_view.phone.belgium")}</p>
        <p>{t("reporting_view.phone.france")}</p>
        <p>{t("reporting_view.phone.spain")}</p>
      </div>
    </div>
  );
}

export default withTranslation()(ReportingView);


function DisplayStatus({ t, isHealthy}) {
  return (
    <div className={`status-box ${isHealthy ? 'status-normal' : 'status-issue'}`}>
      <p>
        <b>{t("reporting_view.status")}:</b> {isHealthy ? t("reporting_view.working_normally") : t("reporting_view.abnormal")}
      </p>
    </div>
  );
}

function DisplayEnergyGraph({t, graphData}) {
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const datasetIndex = context.datasetIndex;
            const value = context.parsed.y;
            
            if (datasetIndex === 0) {
              // First dataset (energy consumption) - show as is
              return `${context.dataset.label}: ${+value.toFixed(2)}`;
            } else if (datasetIndex === 1) {
              // Second dataset (total) - show only the savings value
              const energyValue = context.chart.data.datasets[0].data[context.dataIndex]; 
              const savingsValue = +(value - energyValue).toFixed(2);
              return `${t("reporting_view.savings_graph.label")}: ${savingsValue}`;
            }
            return `${context.dataset.label}: ${value}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
      },
    },
  };

  return (
    <div className="content-box">
      <h2>{t("reporting_view.energy_graph.title")}</h2>
      {graphData.labels.length === 0 ? (
        <p>{t("reporting_view.no_data")}</p>
      ) : (
        <div style={{ height: '300px' }}>
          <Line data={graphData} options={options} />
        </div>
      )}
    </div>
  );
}
